import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { suppliersUrl, supplierByIdUrl } from '../../BaseURL';
import Modal from '../../components/Modal';
import { toastSuccess, swalDangerConfirm, showAxiosError } from '../../utils/swal';

const empty = { name: '', contact_person: '', phone: '', email: '', address: '', notes: '' };

const Suppliers = () => {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    const res = await axios.get(suppliersUrl);
    if (res.data?.success) setRows(res.data.results || []);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) => [r.name, r.contact_person, r.phone, r.email].some((v) => String(v || '').toLowerCase().includes(q)));
  }, [rows, search]);

  const openCreate = () => { setForm(empty); setEditId(null); setOpen(true); setError(null); };
  const openEdit = (s) => {
    setForm({
      name: s.name || '', contact_person: s.contact_person || '', phone: s.phone || '',
      email: s.email || '', address: s.address || '', notes: s.notes || '',
    });
    setEditId(s.id); setOpen(true); setError(null);
  };

  const submit = async () => {
    setError(null); setSaving(true);
    try {
      if (editId) await axios.patch(supplierByIdUrl(editId), form);
      else await axios.post(suppliersUrl, form);
      setOpen(false); await load();
      toastSuccess(editId ? 'Supplier updated' : 'Supplier added');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  const remove = async (s) => {
    const ok = await swalDangerConfirm(`Deactivate ${s.name}?`, 'They will no longer appear in purchase forms.', { confirmButtonText: 'Yes, deactivate' });
    if (!ok) return;
    try {
      await axios.delete(supplierByIdUrl(s.id));
      toastSuccess('Supplier deactivated');
      load();
    } catch (err) { showAxiosError(err); }
  };

  return (
    <>
      <div className="panel">
        <div className="panel-head">
          <h2>Suppliers</h2>
          <div className="panel-toolbar">
            <input className="f-input" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 240 }} />
            <button className="btn-primary" onClick={openCreate}>+ New supplier</button>
          </div>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr><th>Name</th><th>Contact</th><th>Phone</th><th>Email</th><th>Address</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id}>
                  <td><strong>{s.name}</strong></td>
                  <td>{s.contact_person || '—'}</td>
                  <td>{s.phone || '—'}</td>
                  <td>{s.email || '—'}</td>
                  <td>{s.address || '—'}</td>
                  <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn-link" onClick={() => openEdit(s)}>Edit</button>
                    <button className="btn-link" style={{ color: 'var(--danger)' }} onClick={() => remove(s)}>Remove</button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan="6" style={{ color: 'var(--mute)', textAlign: 'center', padding: 24 }}>No suppliers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        title={editId ? 'Edit supplier' : 'New supplier'} open={open} onClose={() => setOpen(false)}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={submit} disabled={saving || !form.name}>{saving ? 'Saving…' : (editId ? 'Save' : 'Create')}</button>
          </>
        }
      >
        {error && <div className="notice">{error}</div>}
        <div className="f-grid f-grid-2">
          <div className="f-row"><label className="f-label">Name *</label><input className="f-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="f-row"><label className="f-label">Contact person</label><input className="f-input" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
          <div className="f-row"><label className="f-label">Phone</label><input className="f-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="f-row"><label className="f-label">Email</label><input className="f-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div className="f-row" style={{ gridColumn: '1 / -1' }}><label className="f-label">Address</label><input className="f-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div className="f-row" style={{ gridColumn: '1 / -1' }}><label className="f-label">Notes</label><input className="f-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
      </Modal>
    </>
  );
};

export default Suppliers;
